import type { FC } from 'react';
import React, { useCallback, useMemo, useRef } from 'react';

import { Button, SortIcon } from '@motorway/mw-highway-code';

import { BulkBidding } from 'Components/pages/VehiclesList/FiltersHeader/BulkBidding/BulkBidding';
import { BulkBiddingModalProvider } from 'Components/pages/VehiclesList/FiltersHeader/BulkBidding/BulkBiddingModalProvider';
import { VehicleListDownloadCsv } from 'Components/pages/VehiclesList/FiltersHeader/VehicleListDownloadCsv/VehicleListDownloadCsv';
import SaleTime from 'Components/VehicleList/SaleTime/SaleTime';
import { getVehiclesCount, joinSortParams } from 'Components/VehicleList/VehicleList.helpers';
import VehicleSort from 'Components/VehicleList/VehicleSort/VehicleSort';
import { PulsatingIcon } from 'Storybook/local/PulsatingIcon/PulsatingIcon';
import { prettyPostcode } from 'Utilities/formatters';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';
import useSaleTimes from 'Utilities/hooks/useSaleTimes';
import useUser from 'Utilities/hooks/useUser';
import useSortOrder from 'Utilities/hooks/vehicleList/useSortOrder/useSortOrder';

import styles from './SaleHeaderV2.module.scss';

interface SaleHeaderV2Props {
	vehicleCount: number;
}

export const SaleHeaderV2: FC<SaleHeaderV2Props> = ({ vehicleCount }) => {
	const { order, sort } = useSortOrder();
	const selected = joinSortParams({ order, sort });
	const vehicleCountText = getVehiclesCount('vehicleList.showingVehicleMobile', vehicleCount);
	const { isSaleActive } = useSaleTimes();
	const isBulkBiddingCsvEnabled = useFeatureToggle(FEATURES.bulkBiddingCsv);
	const user = useUser();
	const sortWrapRef = useRef<HTMLSpanElement>(null);

	const dealerLatLng: [number, number] = useMemo(() => [user?.latitude, user?.longitude], [user]);
	const dealerPostcode: string = useMemo(() => prettyPostcode(user?.postcode), [user]);

	const handleMobileSortClick = useCallback(() => {
		const select = sortWrapRef.current?.querySelector('select');
		if (!select) return;
		const picker = select as HTMLSelectElement & { showPicker?: () => void };
		if (typeof picker.showPicker === 'function') {
			picker.showPicker();
		} else {
			select.focus();
		}
	}, []);

	return isSaleActive ? (
		<header className={styles.container}>
			<div className={styles.left}>
				<div className={styles.timerRow}>
					<span className={styles.pulsatingIcon}>
						<PulsatingIcon />
					</span>
					<span className={styles.saleTimeOverride}>
						<SaleTime />
					</span>
				</div>
				{!!vehicleCount && <span className={styles.vehicleCount}>{vehicleCountText}</span>}
			</div>
			{!!vehicleCount && (
				<div className={styles.right}>
					<span ref={sortWrapRef} className={styles.sortWrap}>
						<VehicleSort
							className={styles.sortPill}
							cypressId="sale-header-v2-sort"
							dealerLatLng={dealerLatLng}
							dealerPostcode={dealerPostcode}
							selected={selected}
						/>
					</span>
					<span className={styles.hideOnDesktop}>
						<Button
							icon={SortIcon}
							label="Sort"
							onClick={handleMobileSortClick}
							showLabel={false}
							size="medium"
							variant="tertiary"
						/>
					</span>
					<span className={styles.hideOnMobile}>
						<VehicleListDownloadCsv />
						{isBulkBiddingCsvEnabled && (
							<BulkBiddingModalProvider>
								<BulkBidding />
							</BulkBiddingModalProvider>
						)}
					</span>
				</div>
			)}
		</header>
	) : null;
};
